import { memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Input } from '@ds';
import { AdminFormRow } from '@ui';
import { MdOutlinePlace } from 'react-icons/md';
import {
  CaseFacilityAutocomplete,
  getRenderFacilityAutocompleteItem,
} from '@features/components/CaseFormBlocks/components';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { EmployeeFormValues } from '@/types';
import { AffiliationType } from '@/api/apis/resources';
import { TXCenter } from '@/api/apis/facilities';
import { OpoOrganization } from '@/api/apis/opo';
import { renderAutocompleteItem } from '@/utils/helpers';
import { useInfiniteTXCentersListQuery } from '@/queries/tx-centers';
import { useInfiniteOpoListQuery } from '@/queries/opo';

type AffiliationFacilityRowProps = {
  type: AffiliationType.TXCenter | AffiliationType.OPO;
  code: string;
  address: string;
  isAffiliationChanged: boolean;
  defaultName?: string;
  isDisabled?: boolean;
  onTxCenterSelect: (value?: TXCenter) => void;
  onOpoSelect: (value?: OpoOrganization) => void;
};

export const AffiliationFacilityRow = memo(
  ({
    type,
    code,
    address,
    isAffiliationChanged,
    defaultName,
    isDisabled,
    onTxCenterSelect,
    onOpoSelect,
  }: AffiliationFacilityRowProps) => {
    const { control } = useFormContext<EmployeeFormValues>();
    const { resolveSubdivisionName } = useCountriesData();
    const renderFacilityItem = getRenderFacilityAutocompleteItem(resolveSubdivisionName);

    const config =
      type === AffiliationType.TXCenter
        ? {
            name: 'affiliationTxCenterId' as const,
            placeholder: 'Select TX Center',
            ariaLabel: 'Select TX Center',
            infiniteListQuery: useInfiniteTXCentersListQuery,
            renderListItem: (item: unknown) => renderFacilityItem(item as TXCenter),
            onSelect: onTxCenterSelect,
            codePlaceholder: 'TX Center Code',
            codeAriaLabel: 'tx center code',
            addressPlaceholder: 'TX Center Address',
            addressAriaLabel: 'tx center address',
          }
        : {
            name: 'affiliationOpoId' as const,
            placeholder: 'Select OPO',
            ariaLabel: 'Select OPO',
            infiniteListQuery: useInfiniteOpoListQuery as unknown as typeof useInfiniteTXCentersListQuery,
            renderListItem: renderAutocompleteItem,
            onSelect: onOpoSelect as (value?: TXCenter | undefined) => void,
            codePlaceholder: 'OPO Code',
            codeAriaLabel: 'opo code',
            addressPlaceholder: 'OPO Address',
            addressAriaLabel: 'opo address',
          };

    return (
      <AdminFormRow title="">
        <CaseFacilityAutocomplete
          name={config.name}
          control={control}
          placeholder={config.placeholder}
          ariaLabel={config.ariaLabel}
          infiniteListQuery={config.infiniteListQuery}
          renderListItem={config.renderListItem}
          onSelect={config.onSelect}
          defaultInputValue={isAffiliationChanged ? '' : defaultName}
          isDisabled={isDisabled}
        />
        <div className="flex gap-3 mt-3">
          <div className="flex-[30%]">
            <Input
              placeholder={config.codePlaceholder}
              aria-label={config.codeAriaLabel}
              type="text"
              isDisabled
              value={code}
            />
          </div>
          <div className="flex-[70%]">
            <Input
              placeholder={config.addressPlaceholder}
              aria-label={config.addressAriaLabel}
              type="text"
              isDisabled
              endContent={<MdOutlinePlace size={20} color="#A3A1A9" />}
              value={address}
            />
          </div>
        </div>
      </AdminFormRow>
    );
  },
);
